/**
 * messageIcons - canonical severity glyph for a `MessageType`.
 *
 * Shared by the bell dropdown (NotificationDropdown) and the toasts
 * (ToastProvider) so the same severity reads the same in both places.
 *
 * The mapping matches `HelpBlurb`'s TONE table — the codebase's established
 * severity-icon convention (also used for "Critical" tiles in
 * AIWorkloadStats and BaseDashboardPage):
 *
 *   error / failure → AlertOctagon   (stop-sign; the most severe)
 *   warning         → AlertTriangle
 *   success         → CheckCircle
 *   info            → Megaphone
 *
 * Two deliberate departures, both about legibility at the 16px the bell rows
 * render at:
 *
 *   - NOT `XCircle` for error/failure. A circled ✕ reads as a dismiss
 *     affordance, which is actively misleading next to the toast's real
 *     close button.
 *   - NOT `Info` (a circled "i") for info. Against `AlertOctagon` it is the
 *     same round outline at small sizes, so the two severities were only
 *     told apart by colour. `Megaphone` has a silhouette nothing else in
 *     this set shares. (`HelpBlurb` keeps `Info` — its icon renders at
 *     20px inside a coloured panel, where the ambiguity doesn't arise.)
 */

import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle,
  Info,
  type LucideIcon,
  Megaphone,
} from 'lucide-react'

import type { MessageType } from '../../types/messaging'

// Record (not a switch) so adding a `MessageType` member fails to typecheck
// here until it is given a glyph.
const MESSAGE_TYPE_ICON: Record<MessageType, LucideIcon> = {
  info: Megaphone,
  success: CheckCircle,
  warning: AlertTriangle,
  failure: AlertOctagon,
  error: AlertOctagon,
}

// @purpose Canonical lucide severity glyph for a messaging `MessageType` (bell dropdown + toasts) — error/failure use AlertOctagon (never XCircle, which reads as a close button) and info uses Megaphone so the two stay distinguishable at 16px. Pair with MESSAGE_TYPE_CONFIG.iconColor.
export const messageTypeIcon = (type: MessageType): LucideIcon =>
  // A wire value outside the union still degrades safely to the neutral glyph.
  MESSAGE_TYPE_ICON[type] ?? Info
